/**
 * LazadaOrdersService — the stored copy of the Lazada marketplace order
 * ledger, in `LAZADA_Orders` on the CRM site.
 *
 * ON THE CRM SITE, beside IGCOM_Orders and TIKTOK_Orders, for the reason both
 * of those are: Lazada is a CRM screen and the Manufacturing site's Migration
 * Valve provisions `iSyn2_` lists only. So it takes a CRM-style plain name and
 * provisions ITSELF from a button on the screen.
 *
 * WHY STORE IT. Lazada's access token is good for seven days and the API hands
 * back a window; the questions the store exists to answer — which months, which
 * provinces, how the mix moves — are questions about years. Nothing about the
 * order ledger can be reconstructed later from a marketplace that has moved on.
 *
 * CHUNKED, APPEND-ONLY, NEWEST WINS. A row per order would be thousands of
 * SharePoint rows read to draw one chart, and a replace-in-place that failed
 * halfway would leave the list holding half of one sync and half of another
 * with no way to tell which half.
 *
 * A sync writes ONLY orders the list does not already agree with
 * (`changedOrders`), so re-syncing an unchanged window costs one read and no
 * writes — and Lazada's item states walk over days, so that check is doing
 * real work rather than being a formality.
 */
import { sp } from "./spClient";
import { isAlreadyExistsError, isFieldMissingError, isListMissingError } from "./spErrors";
import { assertBackfillTarget } from "./backfillGuard";
import {
  LAZADA_ORDERS_LIST, ORDERS_PER_ROW, pack, unpackChunk,
  type ILazadaStore, type ILazadaStoredOrder,
} from "./lazadaOrdersStore";

export * from "./lazadaOrdersStore";

/**
 * Every name here ships with the list spec, so there is no site where one
 * column exists and another does not — and `_CORE` is what a read falls back
 * to when the list exists but its columns do not, which is the state a
 * half-finished provisioning leaves behind. Learned on LAZADA_Tokens:
 * SharePoint rejects the WHOLE `$select` over one missing column, and says
 * "does not exist" in words indistinguishable from a missing list.
 */
const LAZADA_ORDERS_SELECT = "Id,rowsJson,count,fromDate,toDate,syncedBy,syncedAt";
const LAZADA_ORDERS_CORE = "Id,Modified";

export class LazadaOrdersService {
  private typeName?: string;

  private async type(): Promise<string> {
    if (this.typeName) return this.typeName;
    const r = await sp.raw<{ d: { ListItemEntityTypeFullName: string } }>(
      `/_api/web/lists/getbytitle('${LAZADA_ORDERS_LIST}')?$select=ListItemEntityTypeFullName`);
    this.typeName = r.d.ListItemEntityTypeFullName;
    return this.typeName;
  }

  /**
   * Everything on the list, folded to one row per order, newest ordered first.
   *
   * Oldest chunk first so a later sync overrides an earlier one — an order
   * that shipped since last week replaces the pending version of itself rather
   * than appearing twice.
   */
  public async readStore(): Promise<ILazadaStore> {
    const empty = (missing: boolean): ILazadaStore =>
      ({ orders: [], syncedAt: "", syncedBy: "", missing });

    let rows: Record<string, unknown>[];
    try {
      rows = await sp.getListItems<Record<string, unknown>>(
        LAZADA_ORDERS_LIST, { select: LAZADA_ORDERS_SELECT });
    } catch (e) {
      // ORDER MATTERS: a missing COLUMN and a missing LIST both say "does not
      // exist". Ask the narrower question first, or a half-provisioned list is
      // reported as absent and the button offered to fix it is refused for
      // already existing.
      if (isFieldMissingError(e)) {
        await sp.getListItems(LAZADA_ORDERS_LIST, { select: LAZADA_ORDERS_CORE });
        return empty(true);
      }
      if (isListMissingError(e)) return empty(true);
      throw e;
    }

    const at = (r: Record<string, unknown>): string => String(r.syncedAt ?? "");
    const ordered = [...rows].sort(
      (a, b) => at(a).localeCompare(at(b)) || (a.Id as number) - (b.Id as number));

    const byId = new Map<string, ILazadaStoredOrder>();
    let syncedAt = "";
    let syncedBy = "";
    for (const r of ordered) {
      const stamp = at(r);
      if (stamp) { syncedAt = stamp; syncedBy = String(r.syncedBy ?? ""); }
      for (const o of unpackChunk(r.rowsJson)) byId.set(o.id, o);
    }

    const orders = [...byId.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return { orders, syncedAt, syncedBy, missing: false };
  }

  /** Write the orders a sync found, in chunks, reporting progress. */
  public async writeOrders(
    orders: readonly ILazadaStoredOrder[],
    syncedBy: string,
    onProgress?: (done: number, total: number) => void,
  ): Promise<number> {
    // Stated once, at the top, where it can be read: this list is on the
    // allow-list ON PURPOSE and the guard is what says so.
    assertBackfillTarget(LAZADA_ORDERS_LIST);
    if (orders.length === 0) return 0;

    const type = await this.type();
    const syncedAt = new Date().toISOString();
    let done = 0;
    onProgress?.(0, orders.length);

    // Oldest first, so a chunk's fromDate/toDate describe a contiguous slice
    // and the list view reads like a timeline rather than a shuffle.
    const byDate = [...orders].sort((a, b) => a.createdAt.localeCompare(b.createdAt));

    for (let i = 0; i < byDate.length; i += ORDERS_PER_ROW) {
      const chunk = byDate.slice(i, i + ORDERS_PER_ROW);
      await sp.createItem(LAZADA_ORDERS_LIST, {
        __metadata: { type },
        Title: `${syncedAt.slice(0, 10)} · ${chunk.length}`,
        rowsJson: JSON.stringify(chunk.map(pack)),
        count: chunk.length,
        fromDate: (chunk[0]?.createdAt ?? "").slice(0, 10),
        toDate: (chunk[chunk.length - 1]?.createdAt ?? "").slice(0, 10),
        syncedBy,
        syncedAt,
      });
      done += chunk.length;
      onProgress?.(done, byDate.length);
    }
    return done;
  }

  /**
   * Create the list on the CRM site. Explicit user action only.
   *
   * IDEMPOTENT, and that is not a nicety — LAZADA_Tokens taught it. Its first
   * provisioning created the list, had every column refused, and then could
   * not be re-run because the list already existed: the one action offered to
   * fix the problem was the one action that could not run. Provisioning is a
   * sequence of remote writes; any step can fail, so every step is safe to
   * repeat.
   */
  public async provision(onProgress?: (m: string) => void): Promise<void> {
    onProgress?.(`Creating ${LAZADA_ORDERS_LIST}…`);
    try {
      await sp.raw(`/_api/web/lists`, {
        method: "POST",
        body: JSON.stringify({
          __metadata: { type: "SP.List" },
          Title: LAZADA_ORDERS_LIST,
          Description:
            "CRM · Lazada Shop — the stored copy of the Lazada marketplace order ledger, so trend "
            + "and province analysis does not re-read Lazada on every screen load. NO BUYER "
            + "IDENTITY OF ANY KIND: Lazada supplies no customer id and masks names, phones and "
            + "streets before they reach us, so this holds an order's facts plus city and "
            + "province and nothing else. Chunked — rowsJson holds a few hundred orders, "
            + "append-only, newest chunk wins per order. Read by the Lazada screen and nothing else.",
          BaseTemplate: 100,
        }),
      });
    } catch (e) {
      if (!isAlreadyExistsError(e)) throw e;
      onProgress?.(`${LAZADA_ORDERS_LIST} already exists — checking its columns…`);
    }

    const fields: { name: string; kind: "text" | "note" | "number" | "datetime"; indexed?: boolean }[] = [
      { name: "rowsJson", kind: "note" },
      { name: "count", kind: "number" },
      { name: "fromDate", kind: "text" },
      { name: "toDate", kind: "text" },
      { name: "syncedBy", kind: "text" },
      { name: "syncedAt", kind: "datetime", indexed: true },
    ];
    const KIND = { text: 2, note: 3, datetime: 4, number: 9 };
    const url = `/_api/web/lists/getbytitle('${LAZADA_ORDERS_LIST}')/fields`;

    for (const f of fields) {
      onProgress?.(`Adding ${f.name}…`);
      try {
        await sp.raw(url, {
          method: "POST",
          body: JSON.stringify({
            // The declared TYPE has to match the properties being set —
            // SharePoint validates it, not the FieldTypeKind. RichText belongs
            // to SP.FieldMultiLineText and is refused outright on SP.Field.
            __metadata: { type: f.kind === "note" ? "SP.FieldMultiLineText" : "SP.Field" },
            Title: f.name,
            FieldTypeKind: KIND[f.kind],
            Required: false,
            // Rich text stores markup AROUND the value and hands it back
            // wrapped in it, which would corrupt every packed chunk.
            ...(f.kind === "note" ? { RichText: false, NumberOfLines: 6 } : {}),
          }),
        });
      } catch (e) {
        if (!isAlreadyExistsError(e)) throw e;
      }

      // Indexed at creation, while the list is empty. SharePoint refuses to
      // index a list past the view threshold, and this one grows steadily.
      if (f.indexed) {
        try {
          await sp.raw(`${url}/getbytitle('${f.name}')`, {
            method: "POST",
            headers: { "X-HTTP-Method": "MERGE", "If-Match": "*" },
            body: JSON.stringify({ __metadata: { type: "SP.Field" }, Indexed: true }),
          });
        } catch { /* An index that cannot be added is slow, not broken. */ }
      }
    }

    this.typeName = undefined;
    onProgress?.(`${LAZADA_ORDERS_LIST} ready (${fields.length} columns)`);
  }
}

export const lazadaOrdersService = new LazadaOrdersService();
